import { fmtDay } from "../lib/format";
import type { Settings } from "../lib/types";
import { useVault } from "../state/store";
import { Badge, Button, Eyebrow, Select, Switch } from "../ui/ds";
import { usePerspective, useUI } from "./ui";

function Row({ label, desc, children }: { label: React.ReactNode; desc: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="set-row">
      <div className="set-text">
        <div className="set-label">{label}</div>
        <div className="set-desc">{desc}</div>
      </div>
      {children}
    </div>
  );
}

export function SettingsView() {
  const v = useVault();
  const p = usePerspective();
  const { setModal } = useUI();
  const s = v.session.settings;
  const set = (patch: Partial<Settings>) => void v.updateSettings(patch);
  const left = 10 - v.session.backup.used.length;

  return (
    <div className="scroll">
      <div className="page-head">
        <Eyebrow size="sm">Settings</Eyebrow>
        <h1 className="page-h1" style={{ marginBottom: 0 }}>
          Security &amp; sharing
        </h1>
      </div>
      <div className="page-body" style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 760 }}>
        {v.preview ? (
          <div className="banner banner-info">You’re previewing as a client. These are your own settings, and they’re read-only here.</div>
        ) : null}
        <div className="card">
          <div style={{ padding: "20px 24px 4px" }}>
            <Eyebrow size="sm" tone="muted">
              Your account
            </Eyebrow>
          </div>
          <Row label="Master password" desc={`Changed ${fmtDay(v.session.passwordChangedAt)}`}>
            <Button variant="secondary" size="sm" onClick={() => !v.guard() && setModal({ kind: "password" })}>
              Change
            </Button>
          </Row>
          <Row
            label={
              <>
                Two-step verification
                <Badge tone="success" dot>
                  On
                </Badge>
              </>
            }
            desc="Authenticator app · asked every time you unlock"
          >
            <Button variant="secondary" size="sm" onClick={() => !v.guard() && setModal({ kind: "totp" })}>
              Reconfigure
            </Button>
          </Row>
          <Row
            label={
              <>
                Backup codes
                {!v.session.backup.saved ? <Badge tone="warning">Not confirmed saved</Badge> : null}
              </>
            }
            desc={`${left} of 10 left · each works once`}
          >
            <Button variant="secondary" size="sm" onClick={() => !v.guard() && setModal({ kind: "codes" })}>
              View codes
            </Button>
          </Row>
          <Row label="Auto-lock" desc="Lock after this long with no activity">
            <div style={{ width: 160 }}>
              <Select size="sm" label="Auto-lock" options={["1 minute", "5 minutes", "15 minutes", "1 hour", "Never"]} value={s.lock} onChange={(x) => set({ lock: x })} />
            </div>
          </Row>
          <Row label="Clear clipboard" desc="Wipe copied secrets after 30 seconds">
            <Switch checked={s.clip} ariaLabel="Clear clipboard" onChange={(x) => set({ clip: x })} />
          </Row>
        </div>
        {!p.asClient ? (
          <>
            <div className="card">
              <div style={{ padding: "20px 24px 4px" }}>
                <Eyebrow size="sm" tone="muted">
                  Client sharing
                </Eyebrow>
              </div>
              <Row label="Require 2FA for clients" desc="Always on. Nobody opens a shared vault without it.">
                <Switch checked disabled ariaLabel="Require 2FA for clients" />
              </Row>
              <Row label="Require saved backup codes" desc="Clients confirm they've stored their codes before first access">
                <Switch checked={s.requireBackup} ariaLabel="Require saved backup codes" onChange={(x) => set({ requireBackup: x })} />
              </Row>
              <Row label="Default link expiry" desc="For new one-time links">
                <div style={{ width: 160 }}>
                  <Select size="sm" label="Default link expiry" options={["1 hour", "24 hours", "7 days"]} value={s.linkExpiry} onChange={(x) => set({ linkExpiry: x })} />
                </div>
              </Row>
              <Row label="Email alerts" desc="When a client views, adds or edits a credential. Needs an email provider set up first, so it’s off for now.">
                <Switch checked={false} disabled ariaLabel="Email alerts" />
              </Row>
            </div>
            <div className="card">
              <Row label="Encrypted backup" desc="Export every vault to one file, locked with your master password">
                <Button variant="secondary" size="sm" icon="download" onClick={() => setModal({ kind: "export" })}>
                  Export
                </Button>
              </Row>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
